import React from 'react';
import { CircularProgress } from '@mui/material';
import { twMerge } from 'tailwind-merge';

interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  disabled?: boolean;
  icon?: React.ReactNode; // Updated to accept React nodes (e.g., MUI icons)
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  disabled = false,
  icon = null,
  children,
  className,
  onClick,
}) => {
  const baseStyles =
    'inline-flex items-center justify-center font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';
  const variantStyles = {
    primary: 'bg-primary text-white hover:bg-primary-dark focus:ring-primary',
    secondary: 'bg-secondary text-white hover:bg-secondary-dark focus:ring-secondary',
    outline:
      'border border-primary text-primary hover:bg-primary/10 focus:ring-primary',
  };
  const sizeStyles = {
    sm: 'px-3 py-1.5 text-sm gap-1.5',
    md: 'px-4 py-2 text-base gap-2',
    lg: 'px-6 py-3 text-lg gap-2',
  };

  const classes = twMerge(baseStyles, variantStyles[variant], sizeStyles[size], className);

  return (
    <button className={classes} disabled={disabled || isLoading} onClick={onClick}>
      {isLoading ? (
        <CircularProgress
          size={size === 'sm' ? 16 : size === 'md' ? 20 : 24}
          className="mr-2"
          sx={{ color: variant === 'primary' ? 'white' : 'inherit' }}
        />
      ) : (
        icon && <span className="mr-2 flex items-center">{icon}</span>
      )}
      {children}
    </button>
  );
};

export default Button;